(() => {
  const READY_EVENT = 'akd:gang-sheet-ready';

  const formatNumber = (value, digits = 2) => Number(value || 0).toFixed(digits).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const init = (root) => {
    if (root.dataset.akdBuilderReady === 'true') return;
    root.dataset.akdBuilderReady = 'true';

    const variants = JSON.parse(root.querySelector('[data-akd-variants]').textContent || '[]');
    const fixedWidth = Number(root.dataset.fixedWidth || 22.5);
    const form = root.querySelector('[data-akd-form]');
    const variantInput = root.querySelector('[data-akd-variant-id]');
    const lengthSelect = root.querySelector('[data-akd-length]');
    const quantityInput = root.querySelector('[data-akd-quantity]');
    const returnToInput = root.querySelector('[data-akd-return-to]');
    const generatedFileInput = root.querySelector('[data-akd-generated-file]');
    const manifestInput = root.querySelector('[data-akd-manifest]');
    const sheetProperty = root.querySelector('[data-akd-sheet-property]');
    const itemCountProperty = root.querySelector('[data-akd-item-count-property]');
    const sourceInput = root.querySelector('[data-akd-source-files]');
    const dropZones = root.querySelectorAll('[data-akd-drop-zone]');
    const openButtons = root.querySelectorAll('[data-akd-open-builder]');
    const dialog = root.querySelector('[data-akd-dialog]');
    const closeButtons = root.querySelectorAll('[data-akd-close-builder]');
    const useDesignButton = root.querySelector('[data-akd-use-design]');
    const autoBuildButton = root.querySelector('[data-akd-auto-build]');
    const autoSizeInput = root.querySelector('[data-akd-auto-size]');
    const spacingInput = root.querySelector('[data-akd-spacing]');
    const artworkList = root.querySelector('[data-akd-artwork-list]');
    const emptyState = root.querySelector('[data-akd-empty-state]');
    const canvas = root.querySelector('[data-akd-canvas]');
    const canvasWrap = root.querySelector('[data-akd-canvas-wrap]');
    const pageLabel = root.querySelector('[data-akd-page-label]');
    const previousPage = root.querySelector('[data-akd-previous-page]');
    const nextPage = root.querySelector('[data-akd-next-page]');
    const builderStatus = root.querySelector('[data-akd-builder-status]');
    const designStatus = root.querySelector('[data-akd-design-status]');
    const designRow = root.querySelector('.akd-uv__design-row');
    const readyMessage = root.querySelector('[data-akd-ready-message]');
    const actions = root.querySelector('.akd-uv__actions');
    const unitPrice = root.querySelector('[data-akd-unit-price]');
    const totalPrice = root.querySelector('[data-akd-total-price]');
    const selectedSheet = root.querySelector('[data-akd-selected-sheet]');
    const builderSheet = root.querySelector('[data-akd-builder-sheet]');
    const submitButtons = root.querySelectorAll('[data-akd-submit]');
    const downloadButton = root.querySelector('[data-akd-download-proof]');
    const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
    const ctx = canvas.getContext('2d');
    const margin = 0.125;

    const state = {
      items: [],
      placements: [],
      pages: 1,
      currentPage: 0,
      activePlacement: null,
      dragOffset: null,
      generatedFile: null,
      isSubmitting: false,
      dirty: true,
      nextId: 1
    };

    const markDesignPending = () => {
      state.dirty = true;
      designRow.classList.remove('is-ready');
      readyMessage.hidden = true;
      openButtons.forEach((button) => { button.textContent = 'Open AKD Gang Sheet Builder'; });
    };

    const variantsByLength = new Map();
    variants.forEach((variant) => {
      const title = variant.public_title || variant.title || '';
      const match = title.match(/x\s*(\d+(?:\.\d+)?)\s*[\"”]?/i);
      if (match) variantsByLength.set(Number(match[1]), variant);
    });
    const lengths = Array.from(variantsByLength.keys()).sort((a, b) => a - b);

    lengths.forEach((length) => {
      const option = document.createElement('option');
      option.value = String(length);
      option.textContent = `${formatNumber(fixedWidth)}\" × ${formatNumber(length)}\"`;
      lengthSelect.append(option);
    });

    const setStatus = (message, type = '') => {
      builderStatus.textContent = message;
      builderStatus.dataset.type = type;
    };

    const selectedLength = () => Number(lengthSelect.value || lengths[0] || 3);

    const updateProductSelection = () => {
      const length = selectedLength();
      const variant = variantsByLength.get(length);
      const quantity = Math.max(1, state.pages || Number(quantityInput.value || 1));
      if (!variant) return;
      variantInput.value = variant.id;
      quantityInput.value = String(quantity);
      selectedSheet.textContent = `${formatNumber(fixedWidth)}\" × ${formatNumber(length)}\"`;
      if (builderSheet) builderSheet.textContent = `${formatNumber(fixedWidth)}\" × ${formatNumber(length)}\"`;
      const price = Number(variant.price || 0) / 100;
      unitPrice.textContent = money.format(price);
      totalPrice.textContent = money.format(price * quantity);
      sheetProperty.value = `${formatNumber(fixedWidth)} in × ${formatNumber(length)} in; ${quantity} sheet${quantity === 1 ? '' : 's'}`;
    };

    const readPngDpi = async (file) => {
      if (file.type !== 'image/png' && !/\.png$/i.test(file.name)) return null;
      const buffer = await file.slice(0, 65536).arrayBuffer();
      const view = new DataView(buffer);
      if (view.byteLength < 33 || view.getUint32(0) !== 0x89504e47) return null;
      let offset = 8;
      while (offset + 12 <= view.byteLength) {
        const length = view.getUint32(offset);
        const type = String.fromCharCode(view.getUint8(offset + 4), view.getUint8(offset + 5), view.getUint8(offset + 6), view.getUint8(offset + 7));
        if (type === 'pHYs' && length >= 9 && offset + 17 <= view.byteLength) {
          const pixelsPerMeter = view.getUint32(offset + 8);
          const unit = view.getUint8(offset + 16);
          return unit === 1 && pixelsPerMeter ? pixelsPerMeter * 0.0254 : null;
        }
        if (type === 'IEND') break;
        offset += length + 12;
      }
      return null;
    };

    const loadImage = (file) => new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const image = new Image();
      image.onload = () => resolve({ image, url });
      image.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error(`Could not read ${file.name}.`));
      };
      image.src = url;
    });

    const addFiles = async (files) => {
      const accepted = Array.from(files || []).filter((file) => /\.(png|jpe?g)$/i.test(file.name) || ['image/png', 'image/jpeg'].includes(file.type));
      if (!accepted.length) {
        setStatus('Please add transparent PNG or high-resolution JPG artwork.', 'error');
        return;
      }

      const totalBytes = accepted.reduce((sum, file) => sum + file.size, 0) + state.items.reduce((sum, item) => sum + item.file.size, 0);
      if (totalBytes > 80 * 1024 * 1024) {
        setStatus('The combined artwork is over 80 MB. Please reduce file sizes or split the order.', 'error');
        return;
      }

      setStatus(`Reading ${accepted.length} file${accepted.length === 1 ? '' : 's'}…`);
      for (const file of accepted) {
        try {
          const loaded = await loadImage(file);
          const metadataDpi = await readPngDpi(file);
          const assumedDpi = metadataDpi || 300;
          let width = loaded.image.naturalWidth / assumedDpi;
          let height = loaded.image.naturalHeight / assumedDpi;
          const maxWidth = fixedWidth - (margin * 2);
          if (width > maxWidth) {
            const scale = maxWidth / width;
            width *= scale;
            height *= scale;
          }
          state.items.push({
            id: state.nextId++,
            file,
            image: loaded.image,
            url: loaded.url,
            width: Math.max(0.25, width),
            height: Math.max(0.25, height),
            ratio: loaded.image.naturalWidth / loaded.image.naturalHeight,
            quantity: 1,
            metadataDpi
          });
        } catch (error) {
          setStatus(error.message, 'error');
        }
      }
      markDesignPending();
      renderArtworkList();
      autoBuild();
      openDialog();
    };

    const renderArtworkList = () => {
      artworkList.replaceChildren();
      emptyState.hidden = state.items.length > 0;
      state.items.forEach((item) => {
        const card = document.createElement('article');
        card.className = 'akd-gsb__art-card';

        const thumb = document.createElement('img');
        thumb.src = item.url;
        thumb.alt = '';
        thumb.className = 'akd-gsb__art-thumb';

        const details = document.createElement('div');
        details.className = 'akd-gsb__art-details';
        const name = document.createElement('strong');
        name.textContent = item.file.name;
        const pixels = document.createElement('span');
        pixels.textContent = `${item.image.naturalWidth} × ${item.image.naturalHeight}px${item.metadataDpi ? ` • ${Math.round(item.metadataDpi)} DPI metadata` : ' • 300 DPI assumed'}`;
        details.append(name, pixels);

        const fields = document.createElement('div');
        fields.className = 'akd-gsb__art-fields';
        const widthLabel = document.createElement('label');
        widthLabel.textContent = 'Width (in)';
        const widthInput = document.createElement('input');
        widthInput.type = 'number';
        widthInput.min = '0.25';
        widthInput.max = String(fixedWidth - margin * 2);
        widthInput.step = '0.01';
        widthInput.value = formatNumber(item.width);
        widthInput.addEventListener('change', () => {
          item.width = clamp(Number(widthInput.value || item.width), 0.25, fixedWidth - margin * 2);
          item.height = item.width / item.ratio;
          markDesignPending();
          renderArtworkList();
          autoBuild();
        });
        widthLabel.append(widthInput);

        const quantityLabel = document.createElement('label');
        quantityLabel.textContent = 'Quantity';
        const quantity = document.createElement('input');
        quantity.type = 'number';
        quantity.min = '1';
        quantity.max = '999';
        quantity.step = '1';
        quantity.value = String(item.quantity);
        quantity.setAttribute('aria-label', `Quantity for ${item.file.name}`);

        const updateQuantity = (nextQuantity) => {
          item.quantity = clamp(Math.floor(Number(nextQuantity || 1)), 1, 999);
          quantity.value = String(item.quantity);
          markDesignPending();
          autoBuild();
        };

        quantity.addEventListener('change', () => updateQuantity(quantity.value));

        const quantityStepper = document.createElement('div');
        quantityStepper.className = 'akd-gsb__quantity-stepper';

        const decreaseQuantity = document.createElement('button');
        decreaseQuantity.type = 'button';
        decreaseQuantity.className = 'akd-gsb__quantity-button';
        decreaseQuantity.textContent = '−';
        decreaseQuantity.setAttribute('aria-label', `Decrease quantity for ${item.file.name}`);
        decreaseQuantity.title = 'Decrease quantity';
        decreaseQuantity.addEventListener('click', () => updateQuantity(item.quantity - 1));

        const increaseQuantity = document.createElement('button');
        increaseQuantity.type = 'button';
        increaseQuantity.className = 'akd-gsb__quantity-button';
        increaseQuantity.textContent = '+';
        increaseQuantity.setAttribute('aria-label', `Increase quantity for ${item.file.name}`);
        increaseQuantity.title = 'Increase quantity';
        increaseQuantity.addEventListener('click', () => updateQuantity(item.quantity + 1));

        quantityStepper.append(decreaseQuantity, quantity, increaseQuantity);
        quantityLabel.append(quantityStepper);
        fields.append(widthLabel, quantityLabel);

        const meta = document.createElement('div');
        meta.className = 'akd-gsb__art-meta';
        const size = document.createElement('span');
        size.textContent = `Print size ${formatNumber(item.width)}\" × ${formatNumber(item.height)}\"`;
        const effectiveDpi = Math.min(item.image.naturalWidth / item.width, item.image.naturalHeight / item.height);
        const quality = document.createElement('span');
        quality.className = effectiveDpi < 150 ? 'is-warning' : '';
        quality.textContent = `Effective ${Math.round(effectiveDpi)} DPI`;
        meta.append(size, quality);

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'akd-gsb__remove';
        remove.textContent = 'Remove';
        remove.addEventListener('click', () => {
          URL.revokeObjectURL(item.url);
          state.items = state.items.filter((candidate) => candidate.id !== item.id);
          markDesignPending();
          renderArtworkList();
          autoBuild();
        });

        card.append(thumb, details, fields, meta, remove);
        artworkList.append(card);
      });
      itemCountProperty.value = String(state.items.reduce((sum, item) => sum + item.quantity, 0));
    };

    const expandedItems = () => state.items
      .flatMap((item) => Array.from({ length: item.quantity }, (_, copyIndex) => ({ item, copyIndex })))
      .sort((a, b) => (b.item.height * b.item.width) - (a.item.height * a.item.width));

    const packAtLength = (length) => {
      const spacing = clamp(Number(spacingInput.value || 0.125), 0, 2);
      const placements = [];
      let page = 0;
      let x = margin;
      let y = margin;
      let rowHeight = 0;

      for (const entry of expandedItems()) {
        const width = entry.item.width;
        const height = entry.item.height;
        if (width > fixedWidth - margin * 2 || height > length - margin * 2) {
          return { error: `${entry.item.file.name} at ${formatNumber(width)}\" × ${formatNumber(height)}\" cannot fit this sheet size.` };
        }
        if (x + width > fixedWidth - margin + 0.0001) {
          x = margin;
          y += rowHeight + spacing;
          rowHeight = 0;
        }
        if (y + height > length - margin + 0.0001) {
          page += 1;
          x = margin;
          y = margin;
          rowHeight = 0;
        }
        placements.push({
          id: `${entry.item.id}-${entry.copyIndex}`,
          itemId: entry.item.id,
          copyIndex: entry.copyIndex,
          page,
          x,
          y,
          width,
          height
        });
        x += width + spacing;
        rowHeight = Math.max(rowHeight, height);
      }
      return { placements, pages: placements.length ? page + 1 : 1 };
    };

    const autoBuild = () => {
      if (!state.items.length) {
        state.placements = [];
        state.pages = 1;
        state.currentPage = 0;
        updateProductSelection();
        renderCanvas();
        setStatus('Add one or more PNG or JPG files to begin.');
        designStatus.textContent = 'No gang sheet design yet.';
        return;
      }

      let result = null;
      if (autoSizeInput.checked) {
        for (const length of lengths) {
          const candidate = packAtLength(length);
          if (!candidate.error && candidate.pages === 1) {
            lengthSelect.value = String(length);
            result = candidate;
            break;
          }
        }
      }
      if (!result && autoSizeInput.checked && lengths.length) lengthSelect.value = String(lengths[lengths.length - 1]);
      if (!result) result = packAtLength(selectedLength());
      if (result.error && lengths.length) {
        lengthSelect.value = String(lengths[lengths.length - 1]);
        result = packAtLength(selectedLength());
      }
      if (result.error) {
        state.placements = [];
        setStatus(result.error, 'error');
        renderCanvas();
        return;
      }

      state.placements = result.placements;
      state.pages = result.pages;
      state.currentPage = clamp(state.currentPage, 0, state.pages - 1);
      markDesignPending();
      updateProductSelection();
      renderCanvas();
      const count = state.placements.length;
      setStatus(`${count} transfer${count === 1 ? '' : 's'} arranged across ${state.pages} sheet${state.pages === 1 ? '' : 's'}. Drag artwork on the preview for final adjustments.`, 'success');
      designStatus.textContent = `${count} transfer${count === 1 ? '' : 's'} • ${state.pages} × ${formatNumber(fixedWidth)}\" × ${formatNumber(selectedLength())}\" sheet${state.pages === 1 ? '' : 's'}`;
    };

    const renderCanvas = () => {
      const length = selectedLength();
      const maxCssWidth = Math.max(280, Math.min(560, canvasWrap.clientWidth - 24 || 520));
      const cssWidth = maxCssWidth;
      const cssHeight = Math.max(180, cssWidth * (length / fixedWidth));
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(cssWidth * ratio);
      canvas.height = Math.round(cssHeight * ratio);
      canvas.style.width = `${cssWidth}px`;
      canvas.style.height = `${cssHeight}px`;
      const scale = canvas.width / fixedWidth;

      ctx.save();
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#e8edf3';
      ctx.lineWidth = 1;
      for (let inch = 1; inch < fixedWidth; inch += 1) {
        const x = inch * scale;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
        ctx.stroke();
      }
      for (let inch = 1; inch < length; inch += 1) {
        const y = inch * scale;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
        ctx.stroke();
      }

      state.placements.filter((placement) => placement.page === state.currentPage).forEach((placement) => {
        const item = state.items.find((candidate) => candidate.id === placement.itemId);
        if (!item) return;
        const x = placement.x * scale;
        const y = placement.y * scale;
        const width = placement.width * scale;
        const height = placement.height * scale;
        ctx.drawImage(item.image, x, y, width, height);
        ctx.strokeStyle = state.activePlacement === placement.id ? '#c81e2f' : 'rgba(20, 58, 103, 0.55)';
        ctx.lineWidth = state.activePlacement === placement.id ? 4 : 1.5;
        ctx.strokeRect(x, y, width, height);
      });
      ctx.restore();

      pageLabel.textContent = `Sheet ${state.currentPage + 1} of ${state.pages}`;
      previousPage.disabled = state.currentPage === 0;
      nextPage.disabled = state.currentPage >= state.pages - 1;
    };

    const canvasPoint = (event) => {
      const rect = canvas.getBoundingClientRect();
      return {
        x: ((event.clientX - rect.left) / rect.width) * fixedWidth,
        y: ((event.clientY - rect.top) / rect.height) * selectedLength()
      };
    };

    canvas.addEventListener('pointerdown', (event) => {
      const point = canvasPoint(event);
      const placement = state.placements.slice().reverse().find((candidate) => candidate.page === state.currentPage
        && point.x >= candidate.x && point.x <= candidate.x + candidate.width
        && point.y >= candidate.y && point.y <= candidate.y + candidate.height);
      if (!placement) return;
      state.activePlacement = placement.id;
      state.dragOffset = { x: point.x - placement.x, y: point.y - placement.y };
      canvas.setPointerCapture(event.pointerId);
      renderCanvas();
    });

    canvas.addEventListener('pointermove', (event) => {
      if (!state.dragOffset || !state.activePlacement) return;
      const placement = state.placements.find((candidate) => candidate.id === state.activePlacement);
      if (!placement) return;
      const point = canvasPoint(event);
      placement.x = clamp(point.x - state.dragOffset.x, margin, fixedWidth - margin - placement.width);
      placement.y = clamp(point.y - state.dragOffset.y, margin, selectedLength() - margin - placement.height);
      markDesignPending();
      renderCanvas();
    });

    const endDrag = () => {
      state.dragOffset = null;
    };
    canvas.addEventListener('pointerup', endDrag);
    canvas.addEventListener('pointercancel', endDrag);

    const openDialog = () => {
      if (typeof dialog.showModal === 'function' && !dialog.open) dialog.showModal();
      else dialog.setAttribute('open', '');
      document.documentElement.classList.add('akd-gsb-open');
      requestAnimationFrame(renderCanvas);
    };

    const closeDialog = () => {
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      else dialog.removeAttribute('open');
      document.documentElement.classList.remove('akd-gsb-open');
    };

    const manifest = () => ({
      version: 1,
      width: fixedWidth,
      length: selectedLength(),
      pages: state.pages,
      spacing: Number(spacingInput.value || 0),
      items: state.items.map((item) => ({ id: item.id, name: item.file.name, width: item.width, height: item.height, quantity: item.quantity })),
      placements: state.placements.map(({ itemId, copyIndex, page, x, y, width, height }) => ({ itemId, copyIndex, page, x, y, width, height }))
    });

    const buildPdf = async () => {
      if (!window.PDFLib) throw new Error('The print-file generator did not load. Please refresh the page and try again.');
      if (!state.items.length || !state.placements.length) throw new Error('Add artwork and build the gang sheet first.');
      const { PDFDocument } = window.PDFLib;
      const documentPdf = await PDFDocument.create();
      documentPdf.setTitle('AKD Ink UV DTF Gang Sheet');
      documentPdf.setCreator('AKD Ink Gang Sheet Builder');
      documentPdf.setProducer('AKD Ink');
      const embedded = new Map();

      for (const item of state.items) {
        const bytes = await item.file.arrayBuffer();
        const image = item.file.type === 'image/png' || /\.png$/i.test(item.file.name)
          ? await documentPdf.embedPng(bytes)
          : await documentPdf.embedJpg(bytes);
        embedded.set(item.id, image);
      }

      for (let pageIndex = 0; pageIndex < state.pages; pageIndex += 1) {
        const page = documentPdf.addPage([fixedWidth * 72, selectedLength() * 72]);
        state.placements.filter((placement) => placement.page === pageIndex).forEach((placement) => {
          page.drawImage(embedded.get(placement.itemId), {
            x: placement.x * 72,
            y: (selectedLength() - placement.y - placement.height) * 72,
            width: placement.width * 72,
            height: placement.height * 72
          });
        });
      }

      const bytes = await documentPdf.save({ useObjectStreams: true });
      const stamp = new Date().toISOString().replace(/[:.]/g, '-');
      return new File([bytes], `AKD-UV-DTF-gang-sheet-${stamp}.pdf`, { type: 'application/pdf' });
    };

    const preparePrintFile = async () => {
      setStatus('Creating the exact-size print-ready PDF…');
      const file = await buildPdf();
      if (file.size > 100 * 1024 * 1024) throw new Error('The finished PDF is over 100 MB. Please split this into two orders or reduce the source file sizes.');
      const transfer = new DataTransfer();
      transfer.items.add(file);
      generatedFileInput.files = transfer.files;
      state.generatedFile = file;
      state.dirty = false;
      const itemSummary = state.items.map((item) => `${item.quantity}x ${formatNumber(item.width)}x${formatNumber(item.height)} ${item.file.name}`).join('; ');
      manifestInput.value = `v1 | ${state.pages} sheet(s) | ${itemSummary}`.slice(0, 240);
      setStatus(`Print-ready file created: ${(file.size / 1024 / 1024).toFixed(1)} MB.`, 'success');
      return file;
    };

    const downloadProof = async () => {
      try {
        downloadButton.disabled = true;
        const file = state.dirty || !state.generatedFile ? await preparePrintFile() : state.generatedFile;
        const link = document.createElement('a');
        link.href = URL.createObjectURL(file);
        link.download = file.name;
        link.click();
        setTimeout(() => URL.revokeObjectURL(link.href), 1000);
      } catch (error) {
        setStatus(error.message, 'error');
      } finally {
        downloadButton.disabled = false;
      }
    };

    sourceInput.addEventListener('change', async () => {
      const selectedFiles = Array.from(sourceInput.files || []);
      await addFiles(selectedFiles);
      sourceInput.value = '';
    });
    dropZones.forEach((zone) => {
      let depth = 0;
      ['dragenter', 'dragover', 'dragleave', 'drop'].forEach((name) => zone.addEventListener(name, (event) => {
        event.preventDefault();
        event.stopPropagation();
      }));
      zone.addEventListener('dragenter', () => {
        depth += 1;
        zone.classList.add('is-dragging');
      });
      zone.addEventListener('dragleave', () => {
        depth = Math.max(0, depth - 1);
        if (!depth) zone.classList.remove('is-dragging');
      });
      zone.addEventListener('drop', (event) => {
        depth = 0;
        zone.classList.remove('is-dragging');
        addFiles(event.dataTransfer.files);
      });
      zone.addEventListener('click', () => sourceInput.click());
      zone.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          sourceInput.click();
        }
      });
    });

    openButtons.forEach((button) => button.addEventListener('click', openDialog));
    closeButtons.forEach((button) => button.addEventListener('click', closeDialog));
    useDesignButton.addEventListener('click', async () => {
      if (!state.items.length) {
        setStatus('Add artwork before using this design.', 'error');
        return;
      }
      const originalText = useDesignButton.textContent;
      useDesignButton.disabled = true;
      useDesignButton.textContent = 'Preparing print file…';
      try {
        if (!state.placements.length) autoBuild();
        if (!state.placements.length) throw new Error('The artwork does not fit the selected sheet. Adjust the size and try again.');
        if (state.dirty || !state.generatedFile) await preparePrintFile();
        designRow.classList.add('is-ready');
        readyMessage.hidden = false;
        designStatus.classList.remove('is-error');
        openButtons.forEach((button) => { button.textContent = 'Edit gang sheet'; });
        closeDialog();
        requestAnimationFrame(() => actions.scrollIntoView({ behavior: 'smooth', block: 'center' }));
      } catch (error) {
        setStatus(error.message, 'error');
      } finally {
        useDesignButton.disabled = false;
        useDesignButton.textContent = originalText;
      }
    });
    autoBuildButton.addEventListener('click', autoBuild);
    autoSizeInput.addEventListener('change', autoBuild);
    spacingInput.addEventListener('change', autoBuild);
    lengthSelect.addEventListener('change', () => {
      if (autoSizeInput.checked) autoSizeInput.checked = false;
      autoBuild();
    });
    previousPage.addEventListener('click', () => {
      state.currentPage = clamp(state.currentPage - 1, 0, state.pages - 1);
      state.activePlacement = null;
      renderCanvas();
    });
    nextPage.addEventListener('click', () => {
      state.currentPage = clamp(state.currentPage + 1, 0, state.pages - 1);
      state.activePlacement = null;
      renderCanvas();
    });
    downloadButton.addEventListener('click', downloadProof);

    dialog.addEventListener('cancel', () => document.documentElement.classList.remove('akd-gsb-open'));
    window.addEventListener('resize', renderCanvas);

    form.addEventListener('submit', async (event) => {
      if (state.isSubmitting) return;
      event.preventDefault();
      if (!state.items.length || !state.placements.length) {
        designStatus.textContent = 'Please build your gang sheet before adding it to the cart.';
        designStatus.classList.add('is-error');
        openDialog();
        return;
      }
      state.isSubmitting = true;
      submitButtons.forEach((button) => {
        button.disabled = true;
        button.dataset.originalText = button.textContent;
        button.textContent = 'Creating print file…';
      });
      try {
        returnToInput.value = event.submitter && event.submitter.dataset.akdCheckout === 'true' ? '/checkout' : '/cart';
        if (state.dirty || !state.generatedFile) await preparePrintFile();
      const itemSummary = state.items.map((item) => `${item.quantity}x ${formatNumber(item.width)}x${formatNumber(item.height)} ${item.file.name}`).join('; ');
      manifestInput.value = `v1 | ${state.pages} sheet(s) | ${itemSummary}`.slice(0, 240);
        updateProductSelection();
        HTMLFormElement.prototype.submit.call(form);
      } catch (error) {
        state.isSubmitting = false;
        submitButtons.forEach((button) => {
          button.disabled = false;
          button.textContent = button.dataset.originalText || 'Add to cart';
        });
        setStatus(error.message, 'error');
        openDialog();
      }
    });

    if (lengths.length) lengthSelect.value = String(lengths[0]);
    updateProductSelection();
    renderArtworkList();
    renderCanvas();
    root.dispatchEvent(new CustomEvent(READY_EVENT));
  };

  const initAll = () => document.querySelectorAll('[data-akd-gang-builder]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
